import { useState, useEffect, useMemo } from 'react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Calendar, CreditCard, Split, Info, RefreshCw } from 'lucide-react'
import { format, addDays } from 'date-fns'
import { cn } from '@/lib/utils'

export type PaymentPlan = 'full' | 'installments' | 'recurring' | 'custom'

export interface InstallmentConfig {
  count: number
  intervalDays: number
  firstDueDate: string
}

export interface RecurringConfig {
  dayOfMonth: number
  firstDueDate: string
}

export interface PaymentSetupData {
  createPayments: boolean
  paymentPlan: PaymentPlan
  installmentConfig: InstallmentConfig
  recurringConfig: RecurringConfig
}

interface PaymentSetupSectionProps {
  projectValue: number
  billingType: 'hourly' | 'fixed'
  value: PaymentSetupData
  onChange: (data: PaymentSetupData) => void
  disabled?: boolean
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value)
}

export function PaymentSetupSection({
  projectValue,
  billingType,
  value,
  onChange,
  disabled = false,
}: PaymentSetupSectionProps) {
  const handleChange = <K extends keyof PaymentSetupData>(
    key: K,
    val: PaymentSetupData[K]
  ) => {
    onChange({ ...value, [key]: val })
  }

  const handleInstallmentChange = <K extends keyof InstallmentConfig>(
    key: K,
    val: InstallmentConfig[K]
  ) => {
    onChange({
      ...value,
      installmentConfig: { ...value.installmentConfig, [key]: val },
    })
  }

  const handleRecurringChange = <K extends keyof RecurringConfig>(
    key: K,
    val: RecurringConfig[K]
  ) => {
    onChange({
      ...value,
      recurringConfig: { ...value.recurringConfig, [key]: val },
    })
  }

  // Preview installments
  const installmentsPreview = useMemo(() => {
    if (!value.createPayments || value.paymentPlan !== 'installments' || projectValue <= 0) {
      return []
    }

    const { count, intervalDays, firstDueDate } = value.installmentConfig
    const baseAmount = Math.floor((projectValue * 100) / count) / 100
    const remainder = Math.round((projectValue - baseAmount * count) * 100) / 100

    const installments = []
    for (let i = 0; i < count; i++) {
      const amount = i === count - 1 ? baseAmount + remainder : baseAmount
      const dueDate = addDays(new Date(firstDueDate), i * intervalDays)
      installments.push({
        number: i + 1,
        amount,
        dueDate,
      })
    }
    return installments
  }, [value, projectValue])

  // For hourly projects, show simplified payment option
  if (billingType === 'hourly') {
    return (
      <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <Label className="font-medium">Configuração de Pagamento</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Projetos por hora têm pagamentos gerados conforme o tempo é registrado.
          Use a página de Pagamentos para gerenciar manualmente.
        </p>
      </div>
    )
  }

  // Fixed price projects
  return (
    <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <Label className="font-medium">Criar Pagamentos Automaticamente</Label>
        </div>
        <Switch
          checked={value.createPayments}
          onCheckedChange={(checked) => handleChange('createPayments', checked)}
          disabled={disabled || projectValue <= 0}
        />
      </div>

      {projectValue <= 0 && (
        <p className="text-sm text-amber-600 flex items-center gap-1">
          <Info className="h-3.5 w-3.5" />
          Defina o valor do projeto para configurar pagamentos
        </p>
      )}

      {value.createPayments && projectValue > 0 && (
        <>
          <Separator />

          <div className="space-y-3">
            <Label>Como deseja receber?</Label>
            <RadioGroup
              value={value.paymentPlan}
              onValueChange={(val) => handleChange('paymentPlan', val as PaymentPlan)}
              className="space-y-2"
              disabled={disabled}
            >
              <div
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  value.paymentPlan === 'full' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                )}
                onClick={() => handleChange('paymentPlan', 'full')}
              >
                <RadioGroupItem value="full" id="full" />
                <div className="flex-1">
                  <label htmlFor="full" className="font-medium cursor-pointer text-sm">
                    À Vista
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Um único pagamento de {formatCurrency(projectValue)}
                  </p>
                </div>
              </div>

              <div
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  value.paymentPlan === 'installments' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                )}
                onClick={() => handleChange('paymentPlan', 'installments')}
              >
                <RadioGroupItem value="installments" id="installments" />
                <div className="flex-1">
                  <label htmlFor="installments" className="font-medium cursor-pointer text-sm flex items-center gap-1">
                    <Split className="h-3.5 w-3.5" />
                    Parcelado
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Dividir em múltiplas parcelas
                  </p>
                </div>
              </div>

              <div
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  value.paymentPlan === 'recurring' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                )}
                onClick={() => handleChange('paymentPlan', 'recurring')}
              >
                <RadioGroupItem value="recurring" id="recurring" />
                <div className="flex-1">
                  <label htmlFor="recurring" className="font-medium cursor-pointer text-sm flex items-center gap-1">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Recorrente Mensal
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Pagamento mensal automático (cria o próximo ao pagar)
                  </p>
                </div>
              </div>

              <div
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  value.paymentPlan === 'custom' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                )}
                onClick={() => handleChange('paymentPlan', 'custom')}
              >
                <RadioGroupItem value="custom" id="custom" />
                <div className="flex-1">
                  <label htmlFor="custom" className="font-medium cursor-pointer text-sm">
                    Configurar Depois
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Adicionar pagamentos manualmente na aba Financeiro
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {value.paymentPlan === 'full' && (
            <div className="space-y-3 pt-2">
              <div className="space-y-2">
                <Label htmlFor="fullDueDate" className="text-sm">Data de Vencimento</Label>
                <Input
                  id="fullDueDate"
                  type="date"
                  value={value.installmentConfig.firstDueDate}
                  onChange={(e) => handleInstallmentChange('firstDueDate', e.target.value)}
                  disabled={disabled}
                />
              </div>
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Pagamento único</span>
                    <span className="font-bold">{formatCurrency(projectValue)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-muted-foreground mt-1">
                    <span>Vencimento</span>
                    <span>
                      {value.installmentConfig.firstDueDate
                        ? format(new Date(value.installmentConfig.firstDueDate), 'dd/MM/yyyy')
                        : '-'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {value.paymentPlan === 'installments' && (
            <div className="space-y-4 pt-2">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm">Número de Parcelas</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[value.installmentConfig.count]}
                      onValueChange={([val]) => handleInstallmentChange('count', val)}
                      min={2}
                      max={12}
                      step={1}
                      className="flex-1"
                      disabled={disabled}
                    />
                    <Badge variant="outline" className="w-12 justify-center">
                      {value.installmentConfig.count}x
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Intervalo (dias)</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[value.installmentConfig.intervalDays]}
                      onValueChange={([val]) => handleInstallmentChange('intervalDays', val)}
                      min={7}
                      max={60}
                      step={7}
                      className="flex-1"
                      disabled={disabled}
                    />
                    <Badge variant="outline" className="w-12 justify-center">
                      {value.installmentConfig.intervalDays}d
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="firstDueDate" className="text-sm">Primeira Parcela Vence em</Label>
                <Input
                  id="firstDueDate"
                  type="date"
                  value={value.installmentConfig.firstDueDate}
                  onChange={(e) => handleInstallmentChange('firstDueDate', e.target.value)}
                  disabled={disabled}
                />
              </div>

              {installmentsPreview.length > 0 && (
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-3 space-y-2">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      Prévia das Parcelas
                    </div>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                      {installmentsPreview.map((inst) => (
                        <div key={inst.number} className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">
                            {inst.number}ª parcela - {format(inst.dueDate, 'dd/MM/yyyy')}
                          </span>
                          <span className="font-medium">{formatCurrency(inst.amount)}</span>
                        </div>
                      ))}
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between items-center text-sm font-bold">
                      <span>Total</span>
                      <span>{formatCurrency(projectValue)}</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {value.paymentPlan === 'recurring' && (
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="recurringDayOfMonth" className="text-sm">Dia do Mês para Cobrança</Label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[value.recurringConfig.dayOfMonth]}
                    onValueChange={([val]) => handleRecurringChange('dayOfMonth', val)}
                    min={1}
                    max={28}
                    step={1}
                    className="flex-1"
                    disabled={disabled}
                  />
                  <Badge variant="outline" className="w-14 justify-center">
                    Dia {value.recurringConfig.dayOfMonth}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Limitado ao dia 28 para evitar problemas com meses curtos
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recurringFirstDueDate" className="text-sm">Primeiro Pagamento Vence em</Label>
                <Input
                  id="recurringFirstDueDate"
                  type="date"
                  value={value.recurringConfig.firstDueDate}
                  onChange={(e) => handleRecurringChange('firstDueDate', e.target.value)}
                  disabled={disabled}
                />
              </div>

              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <RefreshCw className="h-4 w-4 text-primary" />
                    Pagamento Recorrente
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Valor mensal</span>
                    <span className="font-bold">{formatCurrency(projectValue)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>Primeiro vencimento</span>
                    <span>
                      {value.recurringConfig.firstDueDate
                        ? format(new Date(value.recurringConfig.firstDueDate), 'dd/MM/yyyy')
                        : '-'}
                    </span>
                  </div>
                  <Separator className="my-2" />
                  <p className="text-xs text-muted-foreground">
                    🔄 Ao marcar como pago, o próximo mês será criado automaticamente para o dia {value.recurringConfig.dayOfMonth}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  )
}
